import Category from '@shared/classes/category';
import Direction from '@shared/types/enums/direction';
import Event from '../../../../shared/classes/event';
import Focus from '../../../types/enums/focus';
import FocusUtils from '../../../../shared/utils/focus';
import Main from '../../../types/enums/main';
import Modals from '@shared/types/enums/modals';
import Props from '../../../types/props/main';
import Rail from '../../../classes/rail';
import Slider from '../../../classes/slider';
import { RailType } from '@shared/types/enums/rail';
import { closeFloatingMenu } from '../../../../shared/store/thunks/floatingMenu.thunk';
import { magicRemoteSelector } from '../../../../shared/store/slices/magicRemote.slice';
import { modalSelector } from '../../../../shared/store/slices/modal.slice';
import { routerSelector, setParams } from '../../../store/slices/router.slice';
import { store } from '../../../store/store';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { useRef, useCallback, useEffect, useState } from 'react';
import { userSelector } from '../../../store/slices/user.slice';
import {
  focusSelector,
  setFocus,
  setStack,
} from '../../../store/slices/focus.slice';

const DESCRIPTION_DELAY = 500;

export default function useMain({
  complete,
  forceDescription,
  hideBackground = true,
  preventRefocus,
  rails,
  hasFilters,
  sliders,
  startsInMenu,
  startsInSlider,
  update,
  onPagination,
  onFilterChange,
  onRailChange,
}: Props) {
  const dispatch = useAppDispatch();
  const { focus, stack } = useAppSelector(focusSelector);
  const { enabled, hoveringElement } = useAppSelector(magicRemoteSelector);
  const { current: modal } = useAppSelector(modalSelector);
  const { isAnonymous } = useAppSelector(userSelector);
  const {
    params: { current: currentParams },
  } = useAppSelector(routerSelector);

  const [asset, setAsset] = useState<Event | undefined>(undefined);
  const [filter, setFilter] = useState<Category | undefined>(
    (currentParams?.filter as Category) || undefined
  );
  const [horizontalAnimation, setHorizontalAnimation] = useState(
    ([Focus.LOGIN, Focus.MENU, -1].includes(focus as number) ||
      stack.includes(Focus.MENU)) &&
      startsInMenu
  );
  const [verticalAnimation, setVerticalAnimation] = useState(
    forceDescription ||
      startsInSlider ||
      currentParams?.previousFocus === Focus.SLIDERS ||
      currentParams?.previousFocus === Focus.RAILS_LIST ||
      FocusUtils.evaluateSpecificFocus(
        Focus.RAILS_LIST,
        currentParams?.previousFocus
          ? (currentParams?.previousFocus as Focus[])
          : []
      ) ||
      false
  );

  const completeRef = useRef<boolean>(true);
  const currentRail = useRef<number>(
    (currentParams?.verticalIndex as number) || 0
  );
  const filterRef = useRef(filter);
  const focusRef = useRef(focus);
  const hasLogin = useRef<boolean | undefined>(undefined);
  const hasRails = useRef<boolean>(false);
  const hasSliders = useRef<boolean>(false);
  const paramsRef = useRef(currentParams);
  const prevFocus = useRef<Focus | Focus[] | undefined>(undefined);
  const railsRef = useRef<Rail[]>([]);
  const slidersRef = useRef<Slider[]>([]);

  const lastRail = useRef<number>(0);
  const lastSlider = useRef<number>(0);

  const horizontalIndex = useRef<number>(0);
  const verticalIndex = useRef<number>(
    (currentParams as { verticalIndex: number })?.verticalIndex || 0
  );

  railsRef.current = rails.length
    ? rails
    : (currentParams as { rails: Rail[] })?.rails ||
      (!complete && railsRef.current?.length ? railsRef.current : []);
  slidersRef.current = sliders.length
    ? sliders
    : (currentParams as { sliders: Slider[] })?.sliders ||
      (!complete && slidersRef.current?.length ? slidersRef.current : []);

  completeRef.current =
    complete || !!currentParams?.rails || !!currentParams?.sliders;
  filterRef.current = filter;
  focusRef.current = focus;
  hasLogin.current = isAnonymous;
  hasRails.current = railsRef.current.length > 0;
  hasSliders.current = slidersRef.current.length > 0;
  paramsRef.current = currentParams;
  startsInMenu = startsInMenu && !currentParams?.previousFocus;

  // Display data

  const isFilter =
    railsRef.current[currentRail.current]?.type === RailType.FILTER;

  const displaySliders =
    !forceDescription &&
    ((complete && !!slidersRef.current?.length) || !complete) &&
    focus !== Focus.FLOATING_MENU &&
    !(enabled && hoveringElement) &&
    (!isFilter
      ? !FocusUtils.evaluateFocus(Focus.RAILS_LIST) &&
        !FocusUtils.evaluateFocus(
          parseInt(Focus.RAILS_SUB_LIST + '' + currentRail.current)
        ) &&
        !FocusUtils.evaluateSpecificFocus(
          Focus.RAILS_LIST,
          prevFocus.current ? prevFocus.current : []
        ) &&
        !(
          focus === Focus.MODAL_INPUT &&
          stack.length &&
          stack[0] &&
          FocusUtils.evaluateSpecificFocus(
            Focus.RAILS_LIST,
            stack[0] as Focus | Focus[]
          )
        )
      : true);
  const displayEmpty = !!(
    completeRef.current &&
    !railsRef?.current?.length &&
    !slidersRef?.current?.length
  );
  const displayDescription = !!(
    !FocusUtils.evaluateFocus(Focus.MENU) &&
    verticalAnimation &&
    (modal ||
      (enabled && hoveringElement) ||
      focus === Focus.MODAL_BUTTONS ||
      focus === Focus.MODAL_INPUT ||
      focus === Focus.FLOATING_MENU ||
      FocusUtils.evaluateFocus(Focus.CHANNEL_GUIDE_LOGO) ||
      (!isFilter
        ? FocusUtils.evaluateFocus(Focus.RAILS_LIST) ||
          FocusUtils.evaluateFocus(
            parseInt(Focus.RAILS_SUB_LIST + '' + currentRail.current)
          ) ||
          FocusUtils.evaluateSpecificFocus(
            Focus.RAILS_LIST,
            prevFocus.current ? prevFocus.current : []
          )
        : true))
  );
  const displayBackground =
    !forceDescription &&
    !hideBackground &&
    focus !== Focus.MODAL_BUTTONS &&
    focus !== Focus.MODAL_INPUT &&
    focus !== Focus.SLIDERS &&
    focus !== -1 &&
    !FocusUtils.evaluateFocus(Focus.RAILS_LIST);

  // Display object

  const display = {
    background: displayBackground && displaySliders,
    description: displayDescription && !displaySliders,
    empty: displayEmpty,
    rails:
      completeRef.current &&
      railsRef?.current?.length &&
      railsRef?.current[0]?.data.length,
    sliders: displaySliders,
  };

  // Event handlers

  const onChangeElement = (
    type: Main,
    hIndex?: number | null,
    vIndex?: number,
    animationOffset?: number,
    focus?: number | (string | number)[] | number[] | null
  ) => {
    const { focus: global, router } = store.getState();
    let currentRail = null;
    let direction = Direction.HORIZONTAL;
    const hasIncreased =
      (hIndex || 0) > horizontalIndex.current ||
      (vIndex || 0) > verticalIndex.current;
    horizontalIndex.current = hIndex ?? horizontalIndex.current;
    // Set lastRail on rail change
    if (type === Main.RAILS) {
      verticalIndex.current = vIndex ?? verticalIndex.current;
      lastRail.current = hIndex ?? lastRail.current;
      if (verticalIndex.current < railsRef.current.length) {
        currentRail = railsRef.current[verticalIndex.current];
        if (vIndex !== undefined) direction = Direction.VERTICAL;
      }
    }
    // Set lastSlider on slider change
    else {
      lastSlider.current =
        (hIndex ?? lastSlider.current) % slidersRef.current.length;
    }

    // Update stack parameters on element change

    const stackParams = {
      ...router.params.current,
      currentRail,
      filter: filterRef.current,
      previousFocus: focus || global.focus,
      horizontalIndex: horizontalIndex.current,
      verticalIndex: verticalIndex.current,
      rails: railsRef.current,
      sliders: !slidersRef.current.find((item) => item.type === 'none')
        ? slidersRef.current
        : [],
      animationHorizontalOffset:
        (hIndex ? animationOffset : undefined) ??
        router.params.current?.animationHorizontalOffset ??
        0,
      animationVerticalOffset:
        (vIndex ? animationOffset : undefined) ??
        router.params.current?.animationVerticalOffset ??
        0,
    };
    setTimeout(() => {
      dispatch(setParams(stackParams));
    });

    // If pagination is enabled, trigger its callback
    if (onPagination) {
      const horizontalPivot = Math.ceil(currentRail?.data?.length || 1) - 1;
      const verticalPivot = railsRef.current.length
        ? railsRef.current.length > 2
          ? railsRef.current.length - 2
          : railsRef.current.length - 1
        : 0;
      const shouldPaginate = hasIncreased
        ? direction === Direction.HORIZONTAL
          ? (hIndex || 0) >= horizontalPivot // Load after getting to the end of the rail
          : (vIndex || 0) === verticalPivot // Load 2 items before end
        : false;
      const shouldPopulate =
        direction === Direction.VERTICAL &&
        (currentRail?.data?.length || 0) < 1;
      if (shouldPaginate) {
        onPagination(type, direction, currentRail);
      }
      if (shouldPopulate) {
        onPagination(type, Direction.HORIZONTAL, currentRail);
      }
    }

    // Trigger change element callback
    if (direction === Direction.VERTICAL && onRailChange) {
      onRailChange(currentRail);
    }
  };

  const onItemClick = useCallback(
    (
      horizontalIndex: number,
      verticalIndex: number,
      previousFocus: number | (string | number)[] | number[] | null
    ) => {
      const stackParams = {
        ...currentParams,
        horizontalIndex,
        verticalIndex,
        previousFocus,
        previousStack: stack.length ? stack : undefined,
        rails: railsRef.current,
        sliders: !slidersRef.current.find((item) => item.type === 'none')
          ? slidersRef.current
          : [],
      };
      dispatch(setParams(stackParams));
    },
    [currentParams, focus, stack]
  );

  // Actions

  const outOfBoundsAction = useCallback(
    (listFocus: Focus | number | string, key?: string) => {
      if (!completeRef.current && key !== 'LEFT') return;

      setTimeout(() => {
        switch (key) {
          case 'DOWN':
            // Focus rails from sliders
            if (listFocus === Focus.SLIDERS && hasRails.current) {
              dispatch(
                setFocus([
                  Focus.RAILS_LIST,
                  parseInt(`${Focus.RAILS_SUB_LIST}${currentRail.current}`),
                ])
              );
              onChangeElement(
                Main.RAILS,
                lastRail.current,
                currentRail.current
              );
            }
            break;
          case 'UP':
            // Focus sliders from rails
            if (listFocus === Focus.RAILS_LIST && hasSliders.current) {
              dispatch(setFocus(Focus.SLIDERS));
              onChangeElement(Main.SLIDERS, lastSlider.current, 0);
              break;
            }
            break;
          case 'LEFT':
            // Focus menu or logo from main
            const { focus: focusSlice } = store.getState();
            if (
              listFocus !== Focus.RAILS_LIST &&
              railsRef.current[currentRail.current].type ===
                RailType.GUIDE_CHANNEL
            ) {
              prevFocus.current = [
                Focus.CHANNEL_GUIDE_LOGO,
                parseInt(Focus.CHANNEL_GUIDE_LOGO + '' + currentRail.current),
              ];
              dispatch(setFocus(prevFocus.current));
            } else if (
              focusSlice.focus !== Focus.MENU &&
              listFocus !== Focus.RAILS_LIST
            ) {
              prevFocus.current = focusSlice.focus as Focus | Focus[];
              dispatch(setFocus(Focus.MENU));
            }
        }
      });
    },
    []
  );

  // Effect hooks

  useEffect(() => {
    if (startsInMenu && focusRef.current !== Focus.LOGIN) {
      startsInMenu && dispatch(setFocus(Focus.MENU));
    }
  }, []);

  useEffect(() => {
    if (onFilterChange) {
      onFilterChange(filter?.id);
      setAsset(undefined);
    }
  }, [filter]);

  useEffect(() => {
    if (update) onChangeElement(Main.RAILS, update[0], update[1]);
  }, [update]);

  /* Set initial focus */

  useEffect(() => {
    if (complete) {
      const previousFocus = (paramsRef.current as { previousFocus: number })
        ?.previousFocus;
      const previousStack = (
        paramsRef.current as {
          previousStack: (number | (string | number)[] | number[] | null)[];
        }
      )?.previousStack;
      // Set previous focus if exists
      if (previousFocus || previousStack) {
        if (previousFocus && previousFocus !== -1) {
          dispatch(setFocus(previousFocus));
        }
        previousStack && dispatch(setStack(previousStack));
        return;
      }

      // Set sliders or rails as main focus when coming from menu
      if (!preventRefocus) {
        if (sliders.length) {
          if (!startsInMenu) {
            dispatch(setFocus(Focus.SLIDERS));
          }
        } else if (rails.length) {
          const newFocus = [
            Focus.RAILS_LIST,
            parseInt(`${Focus.RAILS_SUB_LIST}0`),
          ];
          if (!startsInMenu) {
            dispatch(setFocus(newFocus));
          }
        }
      }

      // Update params
      dispatch(
        setParams({
          horizontalIndex: horizontalIndex.current,
          verticalIndex: verticalIndex.current,
          rails: railsRef.current,
          sliders: !slidersRef.current.find((item) => item.type === 'none')
            ? slidersRef.current
            : [],
        })
      );
    }

    return () => {
      dispatch(closeFloatingMenu());
    };
  }, [complete]);

  /* Set initial asset to evaluate */

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined = undefined;
    if (
      rails.length &&
      rails[0]?.data?.length &&
      (!asset || asset.dummy || asset instanceof Category)
    ) {
      let vIndex = Math.min(
        (paramsRef.current?.verticalIndex as number) || 0,
        rails.length
      );
      const hIndex = Math.min(
        (paramsRef.current?.horizontalIndex as number) || 0,
        rails[vIndex]?.data?.length || 0
      );
      let asset = rails[vIndex]?.data[hIndex];
      if (rails.length > vIndex && asset instanceof Category) {
        asset = rails[vIndex + 1]?.data[0];
      }
      timeout = setTimeout(() => {
        setAsset(asset);
      }, DESCRIPTION_DELAY);
    }

    // Update stack params on main page change initialization
    if (complete && !paramsRef.current?.previousFocus) {
      focusRef.current === Focus.SLIDERS && onChangeElement(Main.SLIDERS, 0);
      focusRef.current === Focus.RAILS_LIST ||
        (Array.isArray(focus) &&
          focus?.includes(Focus.RAILS_LIST) &&
          onChangeElement(Main.RAILS, 0, 0));
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [rails, complete]);

  /* Set animation based on focus */

  useEffect(() => {
    // Horizontal animation
    if ([Focus.ADS, Focus.SPLASH].includes(focus as number)) {
      setHorizontalAnimation(true);
    } else {
      setHorizontalAnimation(
        focus === Focus.MENU ||
          ((focus === Focus.LOGIN ||
            focus === Focus.MODAL_BUTTONS ||
            focus === Focus.MODAL_INPUT) &&
            stack.includes(Focus.MENU))
      );
    }
    if (!complete) {
      return;
    }

    // Initial rail index
    if (!Object.keys(currentParams || {}).length) {
      focus === Focus.SLIDERS && onChangeElement(Main.SLIDERS, 0);
      focus === Focus.RAILS_LIST ||
        (Array.isArray(focus) &&
          focus?.includes(Focus.RAILS_LIST) &&
          onChangeElement(Main.RAILS, 0, 0));
    }
    // Vertical animation

    // Display sliders when focusing them
    if (
      hasSliders.current &&
      (focus === Focus.SLIDERS ||
        prevFocus.current === undefined ||
        prevFocus.current === Focus.SLIDERS)
    ) {
      setVerticalAnimation(true);
    }
    // Display description if focusing a rail
    else if (
      FocusUtils.evaluateFocus(Focus.CHANNEL_GUIDE_LOGO) ||
      focus === Focus.RAILS_LIST ||
      focus === Focus.MODAL_INPUT ||
      (Array.isArray(focus) && focus?.includes(Focus.RAILS_LIST)) ||
      (focus === Focus.FLOATING_MENU &&
        stack.flat(1).includes(Focus.RAILS_LIST))
    ) {
      setVerticalAnimation(true);
    }
    // Display description if focusing filter when required, only when promos are available
    else if (
      forceDescription &&
      railsRef.current[currentRail.current]?.type === RailType.FILTER &&
      slidersRef.current.length !== 0
    ) {
      setVerticalAnimation(true);
    }
    // Display sliders if leaving main from filter
    else if (
      (display.sliders &&
        currentRail.current === 0 &&
        Array.isArray(prevFocus.current) &&
        prevFocus.current?.includes(Focus.RAILS_LIST) &&
        railsRef.current[currentRail.current].type === RailType.FILTER) ||
      modal === Modals.RECORDING_DELETE ||
      modal === Modals.RECORDING_REQUEST
    ) {
      setVerticalAnimation(true);
    }
    // Display nothing
    else {
      setVerticalAnimation(false);
    }
  }, [complete, currentParams, focus, stack]);

  return {
    asset,
    completeRef,
    currentRail,
    hasFilters,
    display,
    filter,
    lastRail,
    lastSlider,
    prevFocus,
    railsRef,
    slidersRef,
    horizontalAnimation,
    verticalAnimation,
    onChangeElement,
    onItemClick,
    outOfBoundsAction,
    setAsset: (asset: Event | undefined) => {
      if (!(asset instanceof Category)) {
        setAsset(asset);
      }
    },
    setFilter,
  };
}
